import { useHeadsUpClick } from "@/components/heads-up/context/heads-up-context";
import { SmartNoteRow } from "@/components/heads-up/cards/common/smart-note-row";
import { SmartCardSection, NoteList } from "@/components/heads-up/cards/common/layout";
import { CollapsibleSectionHeader } from "@/components/heads-up/cards/common/collapsible-section-header";
import { useIncrementalReveal } from "@/components/heads-up/cards/common/use-incremental-reveal";
import type { FC } from "react";
import type { Maybe } from "@/domains/common/types";
import type { MentionedByNotesListSmartCard } from "@/components/heads-up/heads-up.types";
import { SeeMoreButton } from "@/components/heads-up/cards/common/see-more-button";
import type { SmartNotePreviewInfo } from "@/components/heads-up/cards/common/smart-note-preview.types";

type Props = {
  card: MentionedByNotesListSmartCard;
  getNotePreviewInfo?: (noteId: string) => Maybe<SmartNotePreviewInfo>;
};

export const MentionedByNotesListSmartCardRenderer: FC<Props> = ({ card, getNotePreviewInfo }) => {
  const clickHandlers = useHeadsUpClick();

  const sectionTitle = card.value.title;
  const items = card.value.items.map(item => ({
    id: item.note_id,
    primary: item.primary_label ?? "Untitled",
  }));

  const { visibleItems, hasMore, showAll, reset } = useIncrementalReveal({
    cardId: card.id,
    items,
    initialCount: 3,
    step: 5,
  });

  const content = (
    <NoteList>
      {visibleItems.map(item => (
        <SmartNoteRow
          key={item.id}
          noteId={item.id}
          primary={item.primary}
          onClick={clickHandlers?.noteClick(item.id)}
          notePreviewInfo={getNotePreviewInfo?.(item.id)}
        />
      ))}
      <SeeMoreButton hasMore={hasMore} onClick={showAll} />
    </NoteList>
  );

  return (
    <SmartCardSection>
      <CollapsibleSectionHeader title={sectionTitle} onCollapseChange={c => c && reset()}>
        {content}
      </CollapsibleSectionHeader>
    </SmartCardSection>
  );
};
